import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Pagination,
  Stack,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  Tabs,
  Tab,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Code as CodeIcon,
  Category as CategoryIcon,
  List as ListIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  getAllValues,
  getAllCategories,
  createCodeValue,
  updateCodeValue,
  deleteCodeValue,
} from "../../api/codeValues";
import CategoryManagement from "./CategoryManagement";

const CodeValuesAdmin = () => {
  const [values, setValues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);

  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Form states
  const [createForm, setCreateForm] = useState({
    category: "",
    code: "",
    value: "",
    description: "",
    displayOrder: 0,
    active: true,
  });

  const [editForm, setEditForm] = useState({
    category: "",
    code: "",
    value: "",
    description: "",
    displayOrder: 0,
    active: true,
  });

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuValueId, setMenuValueId] = useState(null);

  useEffect(() => {
    loadCategories();
    loadValues();
  }, [page, size, categoryFilter, activeFilter]);

  const loadCategories = async () => {
    try {
      const response = await getAllCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const loadValues = async () => {
    setLoading(true);
    try {
      const activeOnly = activeFilter === "" ? null : activeFilter === "active";
      const response = await getAllValues(categoryFilter || null, activeOnly);
      console.log(response);

      setValues(response.data || []);
      setTotalElements(response.data?.length || 0);
      setTotalPages(Math.ceil((response.data?.length || 0) / size));
    } catch (error) {
      console.error("Error loading values:", error);
      toast.error("Failed to load code values");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = useCallback(async () => {
    try {
      await createCodeValue(createForm);
      toast.success("Code value created successfully");
      setCreateDialogOpen(false);
      setCreateForm({
        category: "",
        code: "",
        value: "",
        description: "",
        displayOrder: 0,
        active: true,
      });
      loadValues();
    } catch (error) {
      console.error("Error creating code value:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to create code value"
      );
    }
  }, [createForm]);

  const handleEdit = useCallback(async () => {
    try {
      await updateCodeValue(selectedValue.id, editForm);
      toast.success("Code value updated successfully");
      setEditDialogOpen(false);
      setSelectedValue(null);
      loadValues();
    } catch (error) {
      console.error("Error updating code value:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to update code value"
      );
    }
  }, [selectedValue, editForm]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteCodeValue(selectedValue.id);
      toast.success("Code value deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedValue(null);
      loadValues();
    } catch (error) {
      console.error("Error deleting code value:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to delete code value"
      );
    }
  }, [selectedValue]);

  const handleMenuClick = useCallback((event, valueId) => {
    setAnchorEl(event.currentTarget);
    setMenuValueId(valueId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuValueId(null);
  }, []);

  const handleEditClick = useCallback(
    (value) => {
      setSelectedValue(value);
      setEditForm({
        category: value.category,
        code: value.code,
        value: value.value,
        description: value.description || "",
        displayOrder: value.displayOrder,
        active: value.active,
      });
      setEditDialogOpen(true);
      handleMenuClose();
    },
    [handleMenuClose]
  );

  const handleDeleteClick = useCallback(
    (value) => {
      setSelectedValue(value);
      setDeleteDialogOpen(true);
      handleMenuClose();
    },
    [handleMenuClose]
  );

  const handleCreateFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    setCreateForm((prev) => ({ ...prev, [name]: fieldValue }));
  };

  const handleEditFormChange = useCallback((field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const filteredValues = values.filter((value) => {
    const matchesSearch =
      !searchTerm ||
      value.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      value.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      value.category.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const paginatedValues = filteredValues.slice(page * size, (page + 1) * size);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, width: { xs: "380px", md: "99%" } }}>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }} // vertical on mobile, horizontal on desktop
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }} // align title left on mobile, center vertically on desktop
        mb={3}
        gap={{ xs: 2, md: 0 }} // spacing between elements on mobile
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontSize: "22px" }}
            component="h1"
            gutterBottom
          >
            {/* <CodeIcon sx={{ mr: 2, verticalAlign: "middle" }} /> */}
            Code Values Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage dynamic code/value pairs and categories
          </Typography>
        </Box>
        {activeTab === 0 && (
          <Button
            variant="contained"
            sx={{ width: { md: "18%", xs: "100%" } }}
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            fullWidth={{ xs: true, md: false }} // full width on mobile, auto on desktop
          >
            Add New Code Value
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab icon={<ListIcon />} label="Code Values" iconPosition="start" />
          <Tab
            icon={<SettingsIcon />}
            label="Category Management"
            iconPosition="start"
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      label="Category"
                      sx={{ textAlign: "left" }}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      label="Status"
                      sx={{ textAlign: "left" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadValues}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card>
            <CardContent>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: "70vh" }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Code
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Value
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Display Order
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                      >
                        Created Date
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          right: 0,
                          zIndex: 4,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : paginatedValues.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No code values found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedValues.map((value) => (
                        <TableRow key={value.id} hover>
                          <TableCell>
                            <Chip
                              label={value.category}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {value.code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {value.value}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {value.description || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {value.displayOrder}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={value.active ? "Active" : "Inactive"}
                              color={value.active ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {new Date(value.createdDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              position: "sticky",
                              right: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <IconButton
                              onClick={(e) => handleMenuClick(e, value.id)}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 30, 50, 100]}
                component="div"
                count={filteredValues.length} // total rows from server
                rowsPerPage={size} // page size
                page={page} // current page
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </CardContent>
          </Card>

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() =>
                handleEditClick(values.find((v) => v.id === menuValueId))
              }
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                handleDeleteClick(values.find((v) => v.id === menuValueId))
              }
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>

          {/* create  Dialogs form */}
          <Dialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Create New Code Value</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={createForm.category}
                      name="category"
                      label="category"
                      onChange={handleCreateFormChange}
                    >
                      {categories.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    name="code"
                    value={createForm.code}
                    onChange={handleCreateFormChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Value"
                    name="value"
                    value={createForm.value}
                    onChange={handleCreateFormChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={createForm.description}
                    name="description"
                    onChange={handleCreateFormChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    value={createForm.displayOrder}
                    name="displayOrder"
                    onChange={handleCreateFormChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="active"
                        checked={createForm.active}
                        onChange={handleCreateFormChange}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  handleCreate();
                }}
                variant="contained"
              >
                Create
              </Button>
            </DialogActions>
          </Dialog>
          {/* Editing  Dialogs form */}
          <Dialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Edit Code Value</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={editForm.category}
                      onChange={(e) =>
                        setEditForm({ ...editForm, category: e.target.value })
                      }
                      label="Category"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={editForm.code}
                    onChange={(e) =>
                      setEditForm({ ...editForm, code: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Value"
                    value={editForm.value}
                    onChange={(e) =>
                      setEditForm({ ...editForm, value: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    value={editForm.displayOrder}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        displayOrder: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editForm.active}
                        onChange={(e) =>
                          setEditForm({ ...editForm, active: e.target.checked })
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEdit} variant="contained">
                Update
              </Button>
            </DialogActions>
          </Dialog>
          {/* Deleted  Dialogs form */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          >
            <DialogTitle>Delete Code Value</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this code value? This action
                cannot be undone.
              </Typography>
              {selectedValue && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                  <Typography variant="subtitle2">
                    Category: {selectedValue.category}
                  </Typography>
                  <Typography variant="subtitle2">
                    Code: {selectedValue.code}
                  </Typography>
                  <Typography variant="subtitle2">
                    Value: {selectedValue.value}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleDelete} variant="contained" color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <CategoryManagement />
      )}
    </Box>
  );
};

export default CodeValuesAdmin;
